import { Group, ScrollArea, Stack } from "@mantine/core"
import _ from "lodash"

import { LibraryResponses } from "@tfg/types/endpoints/library"

import { CustomFieldItemCard } from "~/pages/Brand/Products/Details/CustomFieldGraph/CustomFieldItemCard"
import {
  HorizontalDivider,
  VerticalDivider,
} from "~/pages/Brand/Products/Details/CustomFieldGraph/Divider.tsx"

type Props = {
  product: LibraryResponses.ProductDetails
}

export const ProductCustomFieldGraph = ({ product }: Props) => {
  const productItem = product.item

  const sortedComponents = _.sortBy(
    product.components,
    (component) => -component.mainFabric
  )

  const verticalDividerCoveredCardsCount = _.reduce(
    sortedComponents,
    (sum, component, index) => {
      if (index === product.components.length - 1) {
        return sum + 1
      }
      return sum + (component.materials.length || 1)
    },
    0
  )

  const productActivities = productItem.operations.map((o) => o.activityId)

  const productCustomFieldsDefs = product.customFieldDefinitions.filter((cfd) =>
    productActivities.includes(cfd.forActivityId)
  )

  return (
    <ScrollArea w="100%" type="scroll" offsetScrollbars>
      <Group
        gap={0}
        align="flex-start"
        style={{ position: "relative" }}
        mb="xl"
        wrap="nowrap"
      >
        <TraceabilityTreeBranch
          customFieldsDefs={productCustomFieldsDefs}
          hideFirstDivider={true}
          key={`${product.id}`}
          item={productItem}
        />
        {product.components.length > 0 && <HorizontalDivider />}
        <Stack align="flex-start" justify="left">
          {product.components.length > 1 && (
            <VerticalDivider itemCount={verticalDividerCoveredCardsCount} />
          )}
          {sortedComponents.map((component) => {
            const componentActivities = component.item.operations.map(
              (o) => o.activityId
            )

            const componentCustomFieldsDefs =
              product.customFieldDefinitions.filter(
                (x) =>
                  componentActivities.includes(x.forActivityId) &&
                  (!x.onActivityId ||
                    productActivities.includes(x.onActivityId))
              )

            return (
              <Group
                key={component.id}
                gap={0}
                align="flex-start"
                wrap="nowrap"
              >
                <TraceabilityTreeBranch
                  customFieldsDefs={componentCustomFieldsDefs}
                  key={`${component.id}`}
                  item={component.item}
                />
                {component.materials.length > 0 && <HorizontalDivider />}
                <Stack align="flex-start" justify="left">
                  {component.materials.length > 1 && (
                    <VerticalDivider itemCount={component.materials.length} />
                  )}
                  {component.materials.map((material) => {
                    const materialActivities = material.item.operations.map(
                      (o) => o.activityId
                    )
                    const materialCustomFieldsDefs =
                      product.customFieldDefinitions.filter(
                        (x) =>
                          materialActivities.includes(x.forActivityId) &&
                          (!x.onActivityId ||
                            productActivities.includes(x.onActivityId))
                      )
                    return (
                      <Group
                        key={material.id}
                        gap={0}
                        align="flex-start"
                        wrap="nowrap"
                      >
                        <TraceabilityTreeBranch
                          customFieldsDefs={materialCustomFieldsDefs}
                          key={`${material.id}`}
                          item={material.item}
                        />
                      </Group>
                    )
                  })}
                </Stack>
              </Group>
            )
          })}
        </Stack>
      </Group>
    </ScrollArea>
  )
}

type TreeBranchProps = {
  item: LibraryResponses.EnrichedItem
  hideFirstDivider?: boolean
  customFieldsDefs: LibraryResponses.ProductDetails["customFieldDefinitions"]
}

export const TraceabilityTreeBranch = ({
  item,
  hideFirstDivider = false,
  customFieldsDefs,
}: TreeBranchProps) => {
  return (
    <Group key={item?.id} gap={0} align="flex-start" wrap="nowrap">
      {!hideFirstDivider && <HorizontalDivider />}
      <CustomFieldItemCard
        customFieldsDefs={customFieldsDefs}
        itemReference={item?.reference}
        operations={item.operations}
      />
    </Group>
  )
}
